/**
 * Root route table.
 *
 * Composes the app's routes from the three permission contexts plus shared
 * route groups, in matching-priority order:
 * - contextRoutes — admin / depot / tenant areas (see *Routes.ts siblings),
 *   each wrapped in a beforeEnter guard that checks context permission
 * - mainRoutes — landing page and legacy redirects
 * - authRoutes — login and password flows (meta.unguarded: all run without a
 *   session; the authGuard skips them)
 * - errorRoutes / userRoutes — error pages, profile pages
 * - designGuideRoutes — dev-only (Vite alias swaps in an empty array in prod)
 * - catchAll404 — must stay last
 *
 * Conventions:
 * - Every route has a globally unique `name` (vue-router requirement; also
 *   the key for the v5 typed-routes map later).
 * - Components are lazy-loaded unless they are needed for first paint.
 * - `meta` fields are typed in ./router-augments.d.ts.
 */

import { type NavigationGuard, type RouteRecordRaw } from 'vue-router';

import { adminRoute } from '@/router/administrationRoutes';
import { depotRoute } from '@/router/depotRoutes';
// Build-time behavior:
// - non-prod resolves to `designGuideRoutes.ts`
// - prod resolves to `designGuideRoutes.prod.ts` (empty array)
// See alias mapping in `vite.config.ts`.
import { designGuideRoutes } from '@/router/designGuideRoutes';
import { subcontractorRoute } from '@/router/subcontractorRoutes';
import { tenantRoute } from '@/router/tenantRoutes';
import { usePermissionsStore } from '@/stores/permissions';
import type { ContextName } from '@/types/permissions/context';
import Landing from '@/views/landing/LandingContainer.vue';

// Lazy-loaded components for better performance.
// Landing stays eagerly imported: it is the first page after login and part
// of the initial bundle on purpose.
const ChangePwd = () => import('@/views/login/ChangePwd.vue');
const ForceChangePwd = () => import('@/views/login/ForceChangePwd.vue');
const UserLogin = () => import('@/views/login/UserLogin.vue');
const ForgotPwd = () => import('@/views/login/ForgotPwd.vue');
const InsufficientPermissions = () =>
  import('@/views/InsufficientPermissions.vue');
const ProfileContainer = () =>
  import('@/components/account/ProfileContainer.vue');
const UserProfileContainer = () =>
  import('@/components/users/UserProfileContainer.vue');
const NavigationError = () =>
  import('@/components/common/navigation/NavigationError.vue');

/**
 * Per-context entry guard: blocks users without permission for the given
 * context (admin / depots / tenants) from entering its route subtree.
 */
const createContextGuard = (contextName: ContextName): NavigationGuard => {
  return async (to, from, next) => {
    const permissionsStore = usePermissionsStore();
    if (permissionsStore.hasContext(contextName)) {
      next();
    } else {
      next({ name: 'notFound' });
    }
  };
};

// Group routes by their purpose
const contextRoutes: RouteRecordRaw[] = [
  {
    beforeEnter: createContextGuard('admin'),
    ...adminRoute
  },
  {
    beforeEnter: createContextGuard('depots'),
    ...depotRoute
  },
  {
    beforeEnter: createContextGuard('tenants'),
    ...tenantRoute
  },
  {
    beforeEnter: createContextGuard('subcontractors'),
    ...subcontractorRoute
  }
];

const authRoutes: RouteRecordRaw[] = [
  {
    component: ChangePwd,
    name: 'changePwd',
    path: '/changePwd',
    props: (route) => ({ ...route.params }),
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: ForceChangePwd,
    name: 'forceChangePwd',
    path: '/forceChangePwd',
    props: (route) => ({ ...route.params }),
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: UserLogin,
    name: 'login',
    path: '/login',
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: ForgotPwd,
    name: 'forgotPwd',
    path: '/forgotPwd',
    meta: { unguarded: true, layout: 'public' }
  }
];

/**
 * Users with exactly one context skip the landing page and jump straight
 * into that context (Context.path doubles as the context's route name).
 */
const singleContextGuard: NavigationGuard = async (to, from, next) => {
  const permissionsStore = usePermissionsStore();

  if (permissionsStore.hasSingleContext) {
    const { contexts } = permissionsStore;
    const contextRouteName = contexts[0]?.path;
    if (contextRouteName) {
      return next({ name: contextRouteName });
    }
  }

  next();
};

const mainRoutes: RouteRecordRaw[] = [
  {
    component: Landing,
    name: 'landing',
    path: '/landing',
    meta: {
      unguarded: false,
      fillHeight: true,
      hasBasicLayout: true
    },
    beforeEnter: singleContextGuard
  },
  {
    // Legacy deep-link redirect. NOTE: `/dc/*` (vue-router v3 wildcard syntax)
    // stopped matching in v4 — `*` in a static segment is a literal character —
    // so this uses the v4 catch-all param syntax instead.
    name: 'dc',
    path: '/dc/:pathMatch(.*)*',
    redirect: '/'
  },
  {
    name: 'dashboard',
    path: '/',
    redirect: '/landing',
    meta: { unguarded: true }
  }
];

const NotFoundPage = () =>
  import('@/components/errorHandling/NotFoundPage.vue');
const MainErrorComponent = () =>
  import('@/components/errorHandling/MainErrorComponent.vue');
const ErrorHandlingContainer = () =>
  import('@/components/errorHandling/ErrorHandlingContainer.vue');

const errorRoutes: RouteRecordRaw[] = [
  {
    component: InsufficientPermissions,
    name: 'noPermissions',
    path: '/no-permissions'
  },
  {
    component: NotFoundPage,
    name: 'notFound',
    path: '/not-found',
    meta: {
      hasBasicLayout: true,
      fillHeight: true
    }
  },
  {
    children: [
      {
        components: {
          default: MainErrorComponent,
          navigation: NavigationError
        },
        name: 'errorCode',
        path: ':errorCode',
        props: {
          default: true,
          navigation: true
        }
      }
    ],
    components: {
      default: ErrorHandlingContainer
    },
    name: 'error',
    path: '/error'
  }
];

const userRoutes: RouteRecordRaw[] = [
  {
    name: 'account',
    path: '/my',
    meta: {
      hasBasicLayout: true
    },
    children: [
      {
        component: ProfileContainer,
        name: 'profile',
        path: 'profile'
      }
    ]
  },
  {
    name: 'user',
    path: '/users',
    meta: {
      hasBasicLayout: true
    },
    children: [
      {
        component: UserProfileContainer,
        name: 'userProfile',
        path: 'profile/:userHashId',
        props: true
      }
    ]
  }
];

// Combine all route groups
const routes: RouteRecordRaw[] = [
  ...contextRoutes,
  ...mainRoutes,
  ...authRoutes,
  ...errorRoutes,
  ...userRoutes,
  // Always spread this import. In prod, Vite swaps it to an empty array.
  ...designGuideRoutes,
  // Catch-all 404 route - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'catchAll404',
    redirect: '/not-found'
  }
];

export default routes;
